import { memo, useCallback } from 'react';
import { useClient } from 'urql';

import { useTranslation } from '@lyearn/i18n';
import { DownloadOutline } from '@lyearn/icons';
import { AddBlock } from '@lyearn/molecules';
import { Stack } from '@lyearn/ui';
import { getRandomIconSettings } from '@/components/IconSettingsColorPicker/utils';
import fetchQuery, { FetchPolicy } from '@/helper/data-op/fetchQuery';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import DraggableList from '@/modules/corporate/components/DraggableList';

import {
  ProjectStagesDocument,
  ProjectStagesQuery,
} from '../../../../graphql/queries/generated/projectStages';
import { ProjectEditorStageFormValue, ProjectEditorStagesErrorType } from './Editor/config';
import ProjectSelectMenu from './ProjectSelectMenu';
import StageInput from './StageInput';

interface StagesInputProps {
  value: ProjectEditorStageFormValue[];
  onChange: (value: any[]) => void;
  error?: ProjectEditorStagesErrorType;
  currentProjectId?: string;
}

function StagesInput({ value, onChange, error, currentProjectId }: StagesInputProps) {
  const { t } = useTranslation(['Projects']);

  const client = useClient();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();

  const onAddStage = useCallback(() => {
    onChange([
      ...value,
      {
        name: '',
        iconSettings: getRandomIconSettings(),
      },
    ]);
  }, [value, onChange]);

  const onReorder = useCallback(
    ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }) => {
      const newStages = [...value];
      const [removed] = newStages.splice(fromIndex, 1);
      newStages.splice(toIndex, 0, removed);
      onChange(newStages);
    },
    [value, onChange],
  );

  const onImportStages = useCallback(
    async (projectId: string) => {
      const { error, data } = await fetchQuery<ProjectStagesQuery>(
        client,
        ProjectStagesDocument,
        {
          filters: {
            id: projectId,
          },
        },
        { requestPolicy: FetchPolicy.CACHE_FIRST },
      );

      if (error) {
        showDefaultErrorSnackbar();
        return;
      }

      const importedStages: ProjectEditorStageFormValue[] = [];
      data?.file?.stages?.forEach((stage) => {
        if (stage) {
          importedStages.push({
            name: stage.name,
            iconSettings: stage.iconSettings ?? getRandomIconSettings(),
            description: stage.description,
          });
        }
      });
      if (importedStages.length) {
        onChange(importedStages);
      }
    },
    [client, onChange, showDefaultErrorSnackbar],
  );

  return (
    <Stack direction="col" gap={12}>
      {value?.length ? (
        <DraggableList
          allowDropOnStart
          className="flex flex-col gap-12 w-full items-stretch -mt-12"
          renderItem={(index, getDragHandleProps) => {
            const stage = value[index];
            const { onMouseEnter, onMouseLeave } = getDragHandleProps();
            return (
              <StageInput
                key={stage.stageId ?? index}
                error={
                  error?.invalidStagesIndexes?.includes(index) ? error.errorMessage : undefined
                }
                value={stage}
                onChange={(newStage) => {
                  const newStages = [...value];
                  newStages[index] = newStage;
                  onChange(newStages);
                }}
                onDelete={() => {
                  const newStages = value.filter((_, i) => i !== index);
                  onChange(newStages);
                }}
                onMouseEnterDragHandle={onMouseEnter}
                onMouseLeaveDragHandle={onMouseLeave}
              />
            );
          }}
          totalCount={value.length}
          onDrop={onReorder}
        />
      ) : null}
      <Stack alignItems="center" className="w-full" direction="row" gap={12}>
        <AddBlock className="h-48" title={t('Projects:Editor.AddStage')} onClick={onAddStage} />
        <ProjectSelectMenu
          currentProjectId={currentProjectId}
          popoverPosition="right"
          onSelect={onImportStages}>
          <AddBlock
            className="h-48"
            startIcon={<DownloadOutline className="h-20 w-20 text-icon-placeholder" />}
            title={t('Projects:Editor.ImportStagesFromExistingProject')}
          />
        </ProjectSelectMenu>
      </Stack>
    </Stack>
  );
}

export default memo(StagesInput);
